import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useSchool } from '../../context/SchoolContext';
import api from '../../utils/axios';

const StaffList = () => {
  const { school } = useSchool();
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters state
  const [searchTerm, setSearchTerm] = useState('');
  const [department, setDepartment] = useState('all');
  const [status, setStatus] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  /**
   * Fetch staff directory from backend API
   */
  const fetchStaffList = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/api/v1/staff', {
        params: {
          search: searchTerm.trim(),
          department,
          status,
          page,
          limit: 10
        }
      });

      if (response.data?.success) {
        setStaffList(response.data.data || []);
        setTotalPages(response.data.pagination?.totalPages || 1);
      } else {
        setError(response.data?.message || 'Failed to load staff records.');
      }
    } catch (err) {
      console.error('Error fetching staff list:', err);
      setError(err.response?.data?.message || err.message || 'Error loading staff records.');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, department, status, page]);

  useEffect(() => {
    fetchStaffList();
  }, [fetchStaffList]);

  /**
   * Status badge styling resolver
   */
  const getStatusBadge = (staffStatus) => {
    switch (staffStatus?.toLowerCase()) {
      case 'active':
        return 'bg-emerald-100 text-emerald-800 border border-emerald-300';
      case 'resigned':
        return 'bg-amber-100 text-amber-800 border border-amber-300';
      case 'terminated':
        return 'bg-red-100 text-red-800 border border-red-300';
      case 'inactive':
      default:
        return 'bg-slate-100 text-slate-700 border border-slate-300';
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Top Header Navigation Bar */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/dashboard/admin" className="w-9 h-9 rounded-xl bg-slate-100 text-slate-700 hover:bg-slate-200 flex items-center justify-center font-bold transition">
              ←
            </Link>
            <div>
              <h1 className="font-bold text-slate-800 text-lg leading-tight">Staff & HR Management</h1>
              <p className="text-xs text-slate-500">Directory of teachers, administration & operational staff</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Link
              to="/staff/payroll"
              className="bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100 font-semibold px-4 py-2.5 rounded-xl text-sm transition"
            >
              Payroll Management
            </Link>
            <Link
              to="/staff/new"
              style={{ backgroundColor: school?.button_color || 'var(--button-color)' }}
              className="text-white font-semibold px-4 py-2.5 rounded-xl text-sm shadow-sm transition hover:brightness-110 flex items-center gap-2"
            >
              <span className="text-base">+</span> Add Staff Member
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        
        {/* Search & Filter Bar */}
        <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Search Staff</label>
            <input
              type="text"
              placeholder="Name, designation, staff ID..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
              className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:ring-2 focus:ring-primary focus:bg-white"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Department</label>
            <select
              value={department}
              onChange={(e) => {
                setDepartment(e.target.value);
                setPage(1);
              }}
              className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs capitalize"
            >
              <option value="all">All Departments</option>
              <option value="Academics">Academics</option>
              <option value="Administration">Administration</option>
              <option value="Accounts">Accounts</option>
              <option value="Science">Science</option>
              <option value="IT & Security">IT & Security</option>
              <option value="Support Staff">Support Staff</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Status</label>
            <select
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(1);
              }}
              className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs capitalize"
            >
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="resigned">Resigned</option>
              <option value="terminated">Terminated</option>
            </select>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="p-4 bg-red-50 border-l-4 border-red-500 text-red-800 rounded-xl text-sm flex items-center justify-between shadow-sm">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="font-bold text-red-600">✕</button>
          </div>
        )}

        {/* Staff Table Card */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-slate-100 flex items-center justify-between">
            <h2 className="font-bold text-slate-900 text-base">Staff Members Directory</h2>
            <span className="text-xs text-slate-500">Showing page {page} of {totalPages}</span>
          </div>

          {loading ? (
            <div className="p-12 text-center text-slate-400 font-medium">Loading staff records...</div>
          ) : staffList.length === 0 ? (
            <div className="p-12 text-center text-slate-400 font-medium">
              No staff members found matching your search criteria.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs font-sans">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 text-slate-500 uppercase font-semibold">
                    <th className="py-3.5 px-6">Staff ID</th>
                    <th className="py-3.5 px-6">Name</th>
                    <th className="py-3.5 px-4">Designation</th>
                    <th className="py-3.5 px-4">Department</th>
                    <th className="py-3.5 px-4">Phone</th>
                    <th className="py-3.5 px-4">Status</th>
                    <th className="py-3.5 px-6 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-slate-700 font-medium">
                  {staffList.map((st) => (
                    <tr key={st.id} className="hover:bg-slate-50/80 transition border-b border-slate-100">
                      <td className="py-2.5 px-6 font-mono font-bold text-primary">
                        {st.staff_id}
                      </td>
                      <td className="py-2.5 px-6 font-bold text-slate-900 text-sm">
                        {st.name}
                      </td>
                      <td className="py-2.5 px-4 font-semibold text-slate-800">
                        {st.designation}
                      </td>
                      <td className="py-2.5 px-4 text-slate-600">
                        {st.department}
                      </td>
                      <td className="py-2.5 px-4 font-mono text-slate-600">
                        {st.phone}
                      </td>
                      <td className="py-2.5 px-4">
                        <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold capitalize ${getStatusBadge(st.status)}`}>
                          {st.status}
                        </span>
                      </td>
                      <td className="py-2.5 px-6 text-right space-x-2">
                        <Link
                          to={`/staff/${st.id}`}
                          className="bg-slate-100 hover:bg-slate-200 text-slate-800 font-semibold px-3 py-1 rounded-lg transition text-xs inline-block"
                        >
                          View Profile
                        </Link>
                        <Link
                          to={`/staff/payroll/${st.id}/slip`}
                          className="bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-semibold px-3 py-1 rounded-lg border border-indigo-200 transition text-xs inline-block"
                        >
                          Salary Slip
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="p-4 border-t border-slate-100 flex items-center justify-between text-xs bg-slate-50/50">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(p - 1, 1))}
                className="px-3 py-1.5 bg-white border rounded-lg text-slate-700 font-semibold disabled:opacity-40"
              >
                Previous Page
              </button>
              <span className="text-slate-500">Page {page} of {totalPages}</span>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
                className="px-3 py-1.5 bg-white border rounded-lg text-slate-700 font-semibold disabled:opacity-40"
              >
                Next Page
              </button>
            </div>
          )}

        </div>
      </main>
    </div>
  );
};

export default StaffList;
